import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon } from './AdminShell';
import DataTable from './DataTable';
import { Modal, Summary } from './Overlay';
import { text } from './peopleCopy';

/*
 * Currencies — layar `currency` paket client.
 *
 * Prototipe menampilkan lima mata uang yang bisa disunting nama dan kursnya.
 * Kenyataannya 172 baris, tanpa kolom nama, dan kursnya diambil dari
 * api.coinbase.com oleh setBaseCurrency yang men-truncate lalu mengisi ulang
 * SELURUH tabel kurs.
 *
 * Karena itu dua field prototipe sengaja tidak dibuat bisa disunting:
 *   - Nama: bukan data kita. Datang dari Intl.DisplayNames.
 *   - Kurs: angka yang diketik tangan akan tersapu penyegaran berikutnya, jadi
 *     kotak isian di situ adalah janji yang tidak bisa ditepati aplikasi.
 * Yang bisa diubah tinggal yang memang tersimpan: tampil ke pelanggan atau
 * tidak, dan mana yang jadi mata uang dasar.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `query ($currentPage: Int, $limit: Int, $searchList: String, $state: String) {
  getAdminCurrencies(currentPage: $currentPage, limit: $limit, searchList: $searchList, state: $state) {
    count total enabledCount baseCode ratesUpdatedAt status errorMessage
    results { id code name rate enabled base }
  }
}`;

const M_TOGGLE = `mutation ($id: Int, $isEnable: Boolean, $symbol: String) {
  updateCurrency(id: $id, isEnable: $isEnable, symbol: $symbol) { status errorMessage }
}`;

// Menetapkan mata uang dasar JUGA mengambil ulang seluruh tabel kurs. Itu
// sebabnya tombol "Refresh rates" memanggil mutation yang sama dengan id dasar
// yang sedang berlaku — tidak ada endpoint penyegaran tersendiri.
const M_BASE = `mutation ($id: Int) {
  setBaseCurrency(id: $id) { status errorMessage }
}`;

const day = v => {
  const d = new Date(Number(v) || v);
  return isNaN(d) ? null : d.toISOString().slice(0, 10);
};

const daysSince = v => {
  const d = new Date(Number(v) || v);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

class Currencies extends React.Component {
  state = {
    rows: [], count: 0, total: 0, enabledCount: 0, baseCode: null, ratesUpdatedAt: null,
    page: 1, perPage: 25, search: '', visibility: '',
    loading: true, error: null,
    editing: null, enable: false, makingBase: null, refreshing: false,
    saving: false, notice: null
  };

  locale = () => this.props.intl && this.props.intl.locale;
  tx = (en, ar) => text(this.locale(), en, ar);

  componentDidMount() { this.load(); }

  vars = extra => ({
    currentPage: this.state.page,
    limit: this.state.perPage,
    searchList: this.state.search || undefined,
    state: this.state.visibility || undefined,
    ...extra
  });

  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await gql(Q, this.vars()).catch(() => null);
    const res = json && json.data && json.data.getAdminCurrencies;
    if (!res || res.status !== 200) {
      this.setState({ loading: false, error: (res && res.errorMessage) || this.tx('Could not load currencies.', 'تعذر تحميل العملات.') });
      return;
    }
    this.setState({
      rows: res.results || [], count: res.count || 0, total: res.total || 0,
      enabledCount: res.enabledCount || 0, baseCode: res.baseCode,
      ratesUpdatedAt: res.ratesUpdatedAt, loading: false
    });
  };

  setPage = page => this.setState({ page }, () => this.load());
  setPerPage = perPage => this.setState({ perPage, page: 1 }, () => this.load());
  setSearch = search => this.setState({ search, page: 1 }, () => this.load());
  setVisibility = e => this.setState({ visibility: e.target.value, page: 1 }, () => this.load());

  save = async () => {
    const { editing, enable } = this.state;
    this.setState({ saving: true });
    /*
     * DIKIRIM NILAI SEKARANG, BUKAN NILAI YANG DIINGINKAN. updateCurrency
     * menulis `!isEnable` — argumennya diperlakukan sebagai keadaan lama dan
     * dibalik. Mengirim nilai yang diinginkan membuat penyimpanan tampak
     * berhasil sambil menulis kebalikannya. Tombol Save mati kalau pilihannya
     * tidak berubah, jadi membalik selalu menghasilkan nilai yang dipilih.
     *
     * symbol ikut dikirim karena resolver mencarinya untuk memeriksa duplikat.
     */
    const json = await gql(M_TOGGLE,
      { id: editing.id, isEnable: editing.enabled, symbol: editing.code }).catch(() => null);
    const res = json && json.data && json.data.updateCurrency;
    this.setState({ saving: false });
    if (!res || res.status !== 200) {
      this.setState({ notice: { tone: 'danger', text: (res && res.errorMessage) || this.tx('Could not save.', 'تعذر الحفظ.') } });
      return;
    }
    this.setState({
      editing: null,
      notice: {
        tone: 'info',
        text: enable
          ? this.tx(`${editing.code} now appears in the app currency picker.`, `ستظهر ${editing.code} في قائمة العملات بالتطبيق.`)
          : this.tx(`${editing.code} is hidden from customers.`, `تم إخفاء ${editing.code} عن العملاء.`)
      }
    });
    this.load(true);
  };

  makeBase = async () => {
    const row = this.state.makingBase;
    this.setState({ saving: true });
    const json = await gql(M_BASE, { id: row.id }).catch(() => null);
    const res = json && json.data && json.data.setBaseCurrency;
    this.setState({ saving: false });
    if (!res || res.status !== 200) {
      this.setState({ makingBase: null, notice: { tone: 'danger',
        text: (res && res.errorMessage) || this.tx('Could not change the base currency.', 'تعذر تغيير العملة الأساسية.') } });
      return;
    }
    this.setState({ makingBase: null, notice: { tone: 'info',
      text: this.tx(`${row.code} is now the base currency and every rate was refetched against it.`, `أصبحت ${row.code} العملة الأساسية وتم تحديث جميع الأسعار وفقاً لها.`) } });
    this.load(true);
  };

  refresh = async () => {
    const base = this.state.rows.find(r => r.base);
    if (!base) {
      this.setState({ notice: { tone: 'danger',
        text: this.tx('No base currency is set, so there is nothing to refresh rates against.', 'لم يتم تحديد عملة أساسية، لذلك لا توجد أسعار لتحديثها.') } });
      return;
    }
    this.setState({ refreshing: true });
    const json = await gql(M_BASE, { id: base.id }).catch(() => null);
    const res = json && json.data && json.data.setBaseCurrency;
    this.setState({ refreshing: false });
    this.setState({
      notice: (!res || res.status !== 200)
        ? { tone: 'danger', text: (res && res.errorMessage) || this.tx('Could not reach the rate provider.', 'تعذر الوصول إلى مزود الأسعار.') }
        : { tone: 'info', text: this.tx(`Rates refreshed against ${base.code}.`, `تم تحديث الأسعار وفقاً لـ ${base.code}.`) }
    });
    this.load(true);
  };

  columns() {
    return [
      {
        key: 'code', label: this.tx('Code', 'الرمز'),
        render: r => (
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="mono" style={{ fontWeight: 700 }}>{r.code}</span>
            {r.base && <span className="tag amber"><i />{this.tx('Base', 'أساسية')}</span>}
          </span>
        )
      },
      {
        key: 'name', label: this.tx('Currency', 'العملة'), sortable: true,
        render: r => r.name || <span className="muted">{r.code}</span>
      },
      {
        key: 'rate', label: `${this.tx('Rate vs', 'السعر مقابل')} ${this.state.baseCode || this.tx('base', 'الأساسية')}`, align: 'end', sortable: true,
        sortValue: r => (r.rate === null ? -1 : r.rate),
        render: r => r.rate === null
          ? <span className="muted" title={this.tx('The rate provider does not quote this currency', 'مزود الأسعار لا يوفر سعراً لهذه العملة')}>{this.tx('Not quoted', 'غير مسعّرة')}</span>
          : <span className="num">{r.rate.toFixed(4)}</span>
      },
      {
        key: 'enabled', label: this.tx('Shown to customers', 'ظاهرة للعملاء'),
        render: r => (
          <span className={`tag ${r.enabled ? 'ok' : ''}`}>
            <i />{r.enabled ? this.tx('Shown', 'ظاهرة') : this.tx('Hidden', 'مخفية')}
          </span>
        )
      },
      {
        key: 'act', label: '', align: 'end',
        render: r => (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button className="btn icon" type="button" title={this.tx('Edit currency', 'تعديل العملة')}
              aria-label={`${this.tx('Edit', 'تعديل')} ${r.code}`}
              onClick={() => this.setState({ editing: r, enable: r.enabled, notice: null })}>
              <Icon name="edit" />
            </button>
            {!r.base && (
              <button className="btn icon" type="button" title={this.tx('Make base currency', 'تعيين كعملة أساسية')}
                aria-label={`${this.tx('Make', 'تعيين')} ${r.code} ${this.tx('as base currency', 'كعملة أساسية')}`}
                onClick={() => this.setState({ makingBase: r, notice: null })}>
                <Icon name="coins" />
              </button>
            )}
          </span>
        )
      }
    ];
  }

  render() {
    const {
      rows, count, total, enabledCount, baseCode, ratesUpdatedAt,
      page, perPage, loading, error, visibility,
      editing, enable, makingBase, refreshing, saving, notice
    } = this.state;

    const age = daysSince(ratesUpdatedAt);
    const stale = age !== null && age > 7;

    return (
      <AdminShell
        current="currency"
        breadcrumb={[this.tx('Money', 'المال'), this.tx('Currencies', 'العملات')]}
        title={this.tx('Currencies', 'العملات')}
        lead={this.tx(`${baseCode || 'No currency'} is the base. ${enabledCount} of ${total} shown to customers.`, `${baseCode || 'لا توجد عملة'} هي الأساسية. ${enabledCount} من ${total} ظاهرة للعملاء.`)}
        actions={[
          <a className="btn ghost" href="/siteadmin/currency" key="c">{this.tx('Open in classic', 'فتح النسخة الكلاسيكية')}</a>,
          <button className="btn ghost" type="button" key="r"
            onClick={this.refresh} disabled={refreshing}>
            <Icon name="route" />{refreshing ? this.tx('Fetching…', 'جارٍ الجلب…') : this.tx('Refresh rates', 'تحديث الأسعار')}
          </button>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{this.tx('Dismiss', 'إخفاء')}</button>
          </div>
        )}

        {/* Prototipe menulis "Rates refresh daily via cron". Tidak ada cron
            untuk ini di repo — kurs hanya berubah kalau seseorang menekan
            tombolnya. Jadi yang ditampilkan tanggal sungguhan, bukan janji. */}
        {ratesUpdatedAt && (
          <div className={`notice ${stale ? 'danger' : ''}`}>
            <span className="ni">{stale ? '!' : 'i'}</span>
            <span className="nt">
              {this.tx(`Rates last fetched ${day(ratesUpdatedAt)}${age === 0 ? ' (today)' : ` (${age} day${age === 1 ? '' : 's'} ago)`}. There is no scheduled refresh — rates only change when someone presses Refresh rates.`, `آخر تحديث للأسعار ${day(ratesUpdatedAt)}${age === 0 ? ' (اليوم)' : ` (منذ ${age} ${age === 1 ? 'يوم' : 'أيام'})`}. لا يوجد تحديث مجدول — تتغير الأسعار فقط عند الضغط على تحديث الأسعار.`)}
            </span>
          </div>
        )}

        {editing && (
          <Modal
            icon="coins"
            title={`${editing.code}${editing.name ? ` · ${editing.name}` : ''}`}
            sub={this.tx('Only visibility is editable here.', 'يمكن تعديل الظهور فقط هنا.')}
            confirmLabel={this.tx('Save', 'حفظ')}
            busy={saving}
            disabled={enable === editing.enabled}
            onConfirm={this.save}
            onClose={() => this.setState({ editing: null })}
          >
            <Summary pairs={[
              [this.tx('Code', 'الرمز'), <span className="mono">{editing.code}</span>],
              [this.tx('Name', 'الاسم'), editing.name || this.tx('Not known to the system', 'غير معروف للنظام')],
              [`${this.tx('Rate vs', 'السعر مقابل')} ${baseCode || this.tx('base', 'الأساسية')}`,
                editing.rate === null ? this.tx('Not quoted', 'غير مسعّرة') : editing.rate.toFixed(4)],
              [this.tx('Base currency', 'العملة الأساسية'), editing.base ? this.tx('Yes', 'نعم') : this.tx('No', 'لا')]
            ]} />
            <div className="field">
              <label>{this.tx('Show to customers', 'إظهار للعملاء')}</label>
              <select className="inp" value={enable ? '1' : '0'}
                onChange={e => this.setState({ enable: e.target.value === '1' })}>
                <option value="1">{this.tx('Shown in the app currency picker', 'ظاهرة في قائمة العملات بالتطبيق')}</option>
                <option value="0">{this.tx('Hidden', 'مخفية')}</option>
              </select>
            </div>
            <span className="hint">
              {this.tx("The name comes from the device's own locale data and the rate from the provider, so neither is stored here and neither can be edited.", 'الاسم يأتي من بيانات لغة الجهاز والسعر من المزود، لذلك لا يتم تخزين أي منهما هنا ولا يمكن تعديلهما.')}
            </span>
          </Modal>
        )}

        {makingBase && (
          <Modal
            icon="coins" tone="danger"
            title={`${this.tx('Make', 'تعيين')} ${makingBase.code} ${this.tx('the base currency?', 'كعملة أساسية؟')}`}
            sub={this.tx('Every stored rate is deleted and refetched against it.', 'سيتم حذف كل الأسعار المخزنة وإعادة جلبها وفقاً لها.')}
            confirmLabel={this.tx('Change base currency', 'تغيير العملة الأساسية')}
            busy={saving}
            onConfirm={this.makeBase}
            onClose={() => this.setState({ makingBase: null })}
          >
            <div className="notice danger" style={{ marginBottom: 0 }}>
              <span className="ni">!</span>
              <span className="nt">
                {this.tx(`Prices, fares and payouts are stored in ${baseCode || 'the current base'}. Changing the base changes what every existing amount is read as. Do this only if the business genuinely bills in ${makingBase.code}.`, `الأسعار والأجور والدفعات مخزنة بـ ${baseCode || 'العملة الأساسية الحالية'}. تغيير العملة الأساسية يغيّر تفسير كل المبالغ الحالية. افعل ذلك فقط إذا كان النشاط يفوتر فعلياً بـ ${makingBase.code}.`)}
              </span>
            </div>
          </Modal>
        )}

        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : (
            <DataTable
              columns={this.columns()}
              rows={rows}
              count={count}
              page={page}
              perPage={perPage}
              loading={loading}
              onPage={this.setPage}
              onPerPage={this.setPerPage}
              onSearch={this.setSearch}
              searchPlaceholder={this.tx('Search code or currency name', 'ابحث برمز العملة أو اسمها')}
              filters={
                <select className="inp" value={visibility} onChange={this.setVisibility}
                  aria-label={this.tx('Filter by visibility', 'تصفية حسب الظهور')}>
                  <option value="">{this.tx('Visibility: all', 'الظهور: الكل')}</option>
                  <option value="enabled">{this.tx('Shown to customers', 'ظاهرة للعملاء')}</option>
                  <option value="disabled">{this.tx('Hidden', 'مخفية')}</option>
                </select>
              }
              emptyText={this.tx('No currencies match', 'لا توجد عملات مطابقة')}
              emptyHint={this.tx('Try a different code or clear the visibility filter.', 'جرّب رمزاً آخر أو امسح فلتر الظهور.')}
            />
          )}
      </AdminShell>
    );
  }
}

export default injectIntl(Currencies);
